import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { NativeSelect, TextInput } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Property } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, applySorts, useListParams } from "../lib/useListParams";
import { ListShell } from "../components/ListShell";
import { EnrichmentBadge, useEnrichmentForIds } from "../components/Enrichment";
import { RecordLink } from "../components/RecordLink";
import { absent, orAbsent, propertyType } from "../lib/format";
import { recallListPlace, rememberListPlace } from "../lib/listMemory";
import { downloadNarrowed } from "../lib/csvDownload";
import { applyRules } from "../lib/advancedFilters";
import { useCsvColumns } from "../lib/columnChoice";
import { RowActionForm, type RowAction } from "../components/RowActions";
import { DataTable, visibleWith, type ColumnSpec } from "../ui/DataTable";
import { RowMenu } from "../ui/Menu";
import { Word } from "../ui/words";

const FILTERS = ["state", "type", "chargers"] as const;

const COLUMNS =
  "id,name,address_line1,city,state,property_type,unit_count,owner_company_id,owner_company_name,developer_company_id,developer_company_name,project_count,source_count,newest_source_date,has_existing_ev_chargers";

/* The fields a rep wants first. Everything else the view returns can be
   added from the Columns control, and the choice is remembered. */
const CSV_DEFAULT = ["name", "address_line1", "city", "state", "property_type", "unit_count",
             "owner_company_name", "developer_company_name", "project_count", "source_count"] as const;

export function Properties({ session }: { session: SignedIn }) {
  /* Opens on the buildings the engine knows most about, by source count, as
     James set on 2026-10-08, the same order People opens on. It was
     unit_count descending until 2026-09-22, which put the 2,097 buildings
     whose units were never counted in one block at the bottom for a reason
     about our records rather than the building, and newest_source_date from
     then until now. Sorting by a derived count took 3.165 seconds a page, so
     engine migration 180 stores the count on lake.property behind a partial
     index, the way 082 did for people, and the page answers from the index.
     name is the tiebreak in both this query and the index. */
  const params = useListParams("source_count", FILTERS, false, "property");
  const csvColumns = useCsvColumns("properties", "property", CSV_DEFAULT);
  const [rowOpen, setRowOpen] = useState<{ id: string | null; action: RowAction }>({ id: null, action: null });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const placeKey = `properties${location.search}`;
  const [lastOpened, setLastOpened] = useState<string | null>(null);
  const restored = useRef(false);

  const narrowed = (opts?: { count?: "exact"; head?: boolean }, columns?: string) => {
    let q = apiClient(session.idToken).from("property").select(columns ?? COLUMNS, opts);
    if (params.search.trim()) {
      // A property is found by its name or its street, because a rep holding
      // an address has no name to type. The value is wrapped in double quotes
      // because this filter is one raw string where a comma separates the two
      // conditions, and inside the quotes a double quote and a backslash are
      // escaped, which is what PostgREST's own parser expects.
      const t = params.search.trim().replace(/[\\"]/g, (c) => "\\" + c);
      q = q.or(`name.ilike."%${t}%",address_line1.ilike."%${t}%"`);
    }
    if (params.filters["state"]) q = q.eq("state", params.filters["state"]);
    if (params.filters["type"]) q = q.eq("property_type", params.filters["type"]);
    if (params.filters["chargers"] === "yes") q = q.eq("has_existing_ev_chargers", true);
    if (params.filters["chargers"] === "no") q = q.eq("has_existing_ev_chargers", false);
    q = applyRules(q, "property", params.advanced);
    return q;
  };

  const query = useQuery({
    queryKey: ["properties", params.page, params.search, params.sorts, params.filters, params.advanced],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await applySorts(narrowed(), params.sorts)
        // Thousands of buildings tie at the same unit count, and many hold none
        // at all. Without a last key Postgres may order the ties differently per
        // request, so a row can appear on two pages and another on none.
        .order("name", { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Property[];
    },
  });

  const countQuery = useQuery({
    queryKey: ["properties", "count", params.search, params.filters, params.advanced],
    queryFn: async () => {
      const { error, count } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const rows = query.data ?? [];
  const enrichment = useEnrichmentForIds(
    session.idToken, "property",
    rows.map((r) => r.id).filter((v): v is string => Boolean(v)),
  );

  const remember = (id: string) => rememberListPlace(placeKey, { scrollY: window.scrollY, openId: id });
  const open = (p: Property) => {
    if (!p.id) return;
    remember(p.id);
    navigate(`/properties/${p.id}`, { state: { from: "properties" } });
  };

  useEffect(() => {
    if (restored.current || !rows.length) return;
    restored.current = true;
    const place = recallListPlace(placeKey);
    if (!place) return;
    setLastOpened(place.openId);
    window.scrollTo({ top: place.scrollY });
  }, [rows.length, placeKey]);

  if (params.openRecord) return <Navigate to={`/properties/${params.openRecord}`} replace />;

  const columns: ColumnSpec<Property>[] = [
    { id: "name", header: "Property", role: "leading", fields: ["name"], sort: "name", narrowLabel: null, title: true,
      /* A real link, so a rep can open five buildings into tabs with a
         modifier key, which a button cannot do. The row's own click leaves
         links to themselves (src/ui/DataTable.tsx). */
      cell: (p) => p.id ? (
        <Link to={`/properties/${p.id}`} state={{ from: "properties" }} className="link" onClick={() => remember(p.id as string)}>
          {p.name ?? <span className="muted">unnamed</span>}
        </Link>
      ) : (p.name ?? <span className="muted">unnamed</span>) },
    { id: "address_line1", header: "Address", role: "secondary", fields: ["address_line1"], sort: "address_line1", narrowLabel: null,
      cell: (p) => p.address_line1 ?? absent("no street address") },
    { id: "city", header: "City", role: "leading", fields: ["city"], sort: "city", narrowLabel: null, cell: (p) => orAbsent(p.city) },
    { id: "state", header: "State", role: "leading", fields: ["state"], sort: "state", narrowLabel: null, cell: (p) => orAbsent(p.state) },
    { id: "property_type", header: "Type", role: "secondary", fields: ["property_type"], sort: "property_type", narrowLabel: null, cell: (p) => propertyType(p.property_type) },
    { id: "unit_count", header: "Units", role: "secondary", fields: ["unit_count"], align: "num", sort: "unit_count", cell: (p) => p.unit_count ?? absent() },
    { id: "owner", header: "Owner", role: "secondary", fields: ["owner_company_name"], sort: "owner_company_name", narrowLabel: null,
      cell: (p) => <RecordLink to="companies" id={p.owner_company_id} label={p.owner_company_name} /> },
    { id: "developer", header: "Developer", role: "detail", fields: ["developer_company_name"], sort: "developer_company_name",
      cell: (p) => <RecordLink to="companies" id={p.developer_company_id} label={p.developer_company_name} /> },
    { id: "chargers", header: "Chargers", role: "detail", fields: ["has_existing_ev_chargers"], sort: "has_existing_ev_chargers",
      cell: (p) => p.has_existing_ev_chargers === true ? <Word tone="ok">yes</Word>
        : p.has_existing_ev_chargers === false ? <Word tone="quiet">no</Word>
        : absent("unknown", "Whether this property has chargers is not on record") },
    { id: "project_count", header: "Projects", role: "secondary", fields: ["project_count"], align: "num", sort: "project_count", cell: (p) => p.project_count ?? absent() },
    { id: "source_count", header: "Sources", role: "secondary", fields: ["source_count"], align: "num", sort: "source_count", cell: (p) => p.source_count ?? absent() },
    { id: "enrichment", header: "Enrichment", role: "secondary", fields: [], narrowLabel: null,
      cell: (p) => <EnrichmentBadge state={p.id ? enrichment.data?.[p.id]?.state : null} compact /> },
  ];

  return (
    <ListShell
      title="Properties"
      view="property"
      params={params}
      csvColumns={csvColumns}
      total={countQuery.data ?? null}
      totalPending={countQuery.isPending}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["properties"] })}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: "properties",
          columns: csvColumns.chosen,
          total: countQuery.data ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await applySorts(narrowed(undefined, csvColumns.chosen.join(",")), params.sorts)
              .order("name", { ascending: true })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as Property[];
          },
        })
      }
      searchLabel="Search by name or street"
      emptyEverything="No properties match these filters. Change them above, or press Refresh."
      filterLabels={{ state: "State", type: "Type", chargers: "Chargers" }}
      filters={
        <>
          <TextInput
            id="f-state" label="State" description="Two-letter code" w="6rem"
            defaultValue={params.filters["state"] ?? ""} key={params.filters["state"] ?? ""}
            onBlur={(e) => params.set({ filters: { ...params.filters, state: e.currentTarget.value.toUpperCase() } })}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              params.set({ filters: { ...params.filters, state: e.currentTarget.value.toUpperCase() } });
            }}
          />
          <TextInput
            id="f-type" label="Type" w="12rem"
            defaultValue={params.filters["type"] ?? ""} key={`t-${params.filters["type"] ?? ""}`}
            onBlur={(e) => params.set({ filters: { ...params.filters, type: e.currentTarget.value } })}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              params.set({ filters: { ...params.filters, type: e.currentTarget.value } });
            }}
          />
          <NativeSelect
            id="f-chargers" label="Chargers" value={params.filters["chargers"] ?? ""}
            onChange={(e) => params.set({ filters: { ...params.filters, chargers: e.currentTarget.value } })}
            data={[{ value: "", label: "any" }, { value: "yes", label: "has chargers" }, { value: "no", label: "none on record" }]}
          />
        </>
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id ?? ""}
        params={params}
        visible={visibleWith(csvColumns.chosen)}
        openId={lastOpened}
        onOpen={open}
        rowMenu={(p) => (
          <RowMenu items={[
            { label: "Run web enrichment", title: "Search the web for this record", onSelect: () => setRowOpen({ id: p.id ?? null, action: "enrich" }) },
            { label: "Flag", title: "Say this record is wrong. The record stays as it is.", onSelect: () => setRowOpen({ id: p.id ?? null, action: "flag" }) },
          ]} />
        )}
        rowExtra={(p) => rowOpen.id === p.id && rowOpen.action ? (
          <RowActionForm token={session.idToken} entityType="property" entityId={p.id as string}
                         action={rowOpen.action} onDone={() => setRowOpen({ id: null, action: null })} />
        ) : null}
      />
    </ListShell>
  );
}
